import type { ComponentProps, FC } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Shop } from 'graphql-ts-types';
import type { FlexDirectionProps, FlexWrapProps } from 'styled-system';
import { compose, height, marginBottom, textAlign, themeGet } from 'styled-system';

import { useLocalization } from 'common/providers/LocaleProvider';
import h24LogoSvg from '../../assets/images/home-24-logo.svg';
import type { SpaceProps } from '../../theme/system-types';
import { wordBreak } from '../../theme/system-utilities';
import { dispatchTrackingEventGA4 } from '../../tracking/dispatchTrackingEventGA4';
import type { TrackingEventName, TrackingParams } from '../../types/tracking';
import { ButtonBase } from '../Button';
import { Flex } from '../Flex';
import { linkStyles } from '../Link';
import Star from '../Rating/Star';
import type { SellerContactParams } from '../SellerContactContent/utils';
import { CtaType } from '../SellerContactContent/utils';
import { useSellerDrawer } from '../SellerDrawer';
import type { SellerDrawerTracking, SellerRatings } from '../SellerDrawer/types';
import type { CommonTypographyProps } from '../Typography';
import { BodyBoldBB2, BodyRegularBR2, LinkRegularLR2 } from '../Typography';

interface SellerInformationTextProps
  extends Pick<Shop, 'name' | 'id'>, SpaceProps, FlexDirectionProps, FlexWrapProps, SellerRatings {
  isHome24: boolean;
  textLabel: string;
  LabelComponent?: FC<CommonTypographyProps>;
  ButtonComponent?: FC<CommonTypographyProps>;
  tracking?: SellerDrawerTracking;
  withRatingsStar?: boolean;
  trackingParams?: { event: TrackingEventName } & TrackingParams;
  withSellerKPIs: boolean;
  withH24Logo?: boolean;
  ctaType?: CtaType;
  sellerContactParams?: SellerContactParams;
}

const H24_LOGO_HEIGHT = 19;

const containerStyles: ComponentProps<typeof Flex> = {
  mb: 'sp_12',
  alignItems: 'center',
  flexWrap: 'wrap',
};

const LinkButton = styled(ButtonBase)<any>`
  ${linkStyles};
  ${textAlign};
  ${wordBreak};
`;

const linkButtonStyles: ComponentProps<typeof LinkButton> = {
  textDecoration: 'underline',
  textAlign: 'left',
  styled: true,
  wordBreak: 'break-word',
  borderRadius: 'medium',
};

const LogoButton = styled(ButtonBase)`
  outline-offset: 2px;

  &:hover {
    outline: 1px solid ${themeGet('colors.sys.neutral.border.hover')};
  }

  &:active {
    outline: 2px solid ${themeGet('colors.sys.primary.border.default')};
  }
`;

const Home24Logo = styled.img(compose(height, marginBottom));

const SellerInformationText = ({
  name,
  isHome24,
  id: shopId,
  textLabel,
  tracking,
  ratings,
  trackingParams,
  withRatingsStar = true,
  withSellerKPIs,
  LabelComponent = BodyRegularBR2,
  ButtonComponent = LinkRegularLR2,
  withH24Logo = false,
  ctaType = CtaType.ViewProducts,
  sellerContactParams,
  ...customProps
}: SellerInformationTextProps) => {
  const { r, t } = useLocalization();
  const { openDrawer } = useSellerDrawer({
    isHome24,
    shopId,
    withSellerKPIs,
    tracking,
    ctaType,
    sellerContactParams,
  });

  const openSellerDrawer = () => {
    if (trackingParams) {
      dispatchTrackingEventGA4(trackingParams);
    }
    openDrawer();
  };

  const ariaLabel =
    withRatingsStar && ratings
      ? r.t('h24_sold_by_link_label', { seller: name, ratings: ratings.average })
      : `${t('h24_sold_by')} ${name}`;

  return (
    <Flex {...containerStyles} {...customProps}>
      <LabelComponent mr="sp_4">{textLabel}:</LabelComponent>
      {isHome24 && withH24Logo ? (
        <LogoButton borderRadius="small" onClick={openSellerDrawer} aria-label={ariaLabel}>
          <Home24Logo src={h24LogoSvg} height={H24_LOGO_HEIGHT} alt="" marginBottom="sp_2" />
        </LogoButton>
      ) : (
        <LinkButton onClick={openSellerDrawer} {...linkButtonStyles} aria-label={ariaLabel}>
          <ButtonComponent as="span">{name}</ButtonComponent>
        </LinkButton>
      )}
      {withRatingsStar && ratings && (
        <Flex alignItems="center">
          <BodyBoldBB2 ml="sp_4">{ratings.average.toFixed(1)}</BodyBoldBB2>
          <Star ml="sp_4" size={[12, 14]} isFull />
        </Flex>
      )}
    </Flex>
  );
};

export default SellerInformationText;
